import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Download,
  File,
  Calendar,
  User,
  Server
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const History = () => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => {
    setIsAdmin(user?.role === 'admin' || user?.role === 'super admin');
  }, [user]);

  // Pobierz historię uploadów
  const { data: historyData, isLoading, error, refetch } = useQuery(
    ['uploadHistory', currentPage, isAdmin],
    async () => {
      const endpoint = isAdmin ? '/nas/history/all' : '/nas/history';
      const response = await api.get(`${endpoint}?limit=${itemsPerPage}&skip=${(currentPage - 1) * itemsPerPage}`);
      return response.data;
    },
    {
      refetchInterval: 30000, // Odświeżaj co 30 sekund
      staleTime: 10000 // Dane są świeże przez 10 sekund
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Zakończony';
      case 'error':
        return 'Błąd';
      case 'pending':
        return 'W trakcie';
      default:
        return 'Nieznany';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (duration) => {
    if (!duration) return '-';
    if (duration < 1000) return `${duration}ms`;
    if (duration < 60000) return `${(duration / 1000).toFixed(1)}s`;
    return `${(duration / 60000).toFixed(1)}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Błąd pobierania historii
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const history = historyData?.history || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Clock className="w-4 h-4 mr-2" />
          Odśwież
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <File className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Wszystkie pliki
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {history.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Zakończone
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {history.filter(h => h.status === 'success').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Błędy
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {history.filter(h => h.status === 'error').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    W trakcie
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {history.filter(h => h.status === 'pending').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Historia */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Lista uploadów
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {isAdmin ? 'Wszystkie uploady w systemie' : 'Twoje uploady'}
          </p>
        </div>
        
        {history.length === 0 ? (
          <div className="text-center py-12">
            <File className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Brak historii</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isAdmin ? 'Brak uploadów w systemie' : 'Nie masz jeszcze żadnych uploadów'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {history.map((item, index) => (
              <li key={index} className="px-4 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(item.status)}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {item.fileName}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {getStatusText(item.status)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span>{formatFileSize(item.fileSize)}</span>
                          <span>•</span>
                          <span>{item.fileType.toUpperCase()}</span>
                          {item.duration && (
                            <>
                              <span>•</span>
                              <span>{formatDuration(item.duration)}</span>
                            </>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          {item.folderPath}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-900">
                        {isAdmin && item.user ? (
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            {item.user.username}
                          </div>
                        ) : null}
                        <div className="flex items-center mt-1">
                          <Server className="w-4 h-4 mr-1" />
                          {item.device?.name || 'Nieznane urządzenie'}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(item.createdAt)}
                        </div>
                      </div>
                    </div>
                    
                    {item.status === 'error' && item.errorMessage && (
                      <div className="text-right">
                        <div className="text-xs text-red-600 max-w-xs truncate" title={item.errorMessage}>
                          {item.errorMessage}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Paginacja */}
      {history.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Strona {currentPage} z {Math.ceil(history.length / itemsPerPage)}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Poprzednia
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={history.length < itemsPerPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Następna
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
